import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * Cluster manifests (part 10) — the deployment blueprint as evidence, not as live state.
 *
 * Each row is one file in infra/k8s with the SHA-256 of the exact bytes and the validator that
 * accepted it. The banner says "not run on a cluster" because that is true: the manifests were
 * parsed, rendered with kustomize and checked with kubeconform, but no Kubernetes API was
 * contacted and nothing was scheduled. The page never claims otherwise, even if every row is
 * green.
 *
 * The route is prepared but deliberately not wired: server.go and App.tsx are out of scope for
 * this part. Once mounted, the import and one Route line in App.tsx are all that is missing.
 */

type ValidationResult = 'valid' | 'invalid' | 'skipped' | ''

interface Validation {
  result: ValidationResult
  validator: string
  detail: string
  validated_at?: string
}

interface ManifestRow {
  path: string
  kind: string
  name: string
  component: string
  purpose: string
  in_kustomization: boolean
  sha256: string
  bytes: number
  validation: Validation
}

interface Summary {
  total: number
  valid: number
  invalid: number
  pending: number
  skipped: number
  all_valid: boolean
}

interface Payload {
  manifests: ManifestRow[]
  summary: Summary
  validators: string[]
  latest_validation?: string
  cluster_run: boolean
  note: string
}

const QUERY_KEY = ['cluster-manifests']

function StatusChip({ result }: { result: ValidationResult }) {
  if (result === 'valid') {
    return <span className="sev sev-low"><i className="sev-dot" />ผ่าน</span>
  }
  if (result === 'invalid') {
    return <span className="sev sev-critical"><i className="sev-dot" />ไม่ผ่าน</span>
  }
  if (result === 'skipped') {
    return <span className="chip">ข้าม</span>
  }
  return <span className="sev sev-medium"><i className="sev-dot" />ยังไม่ตรวจ</span>
}

export function Cluster() {
  const [filter, setFilter] = useState('')
  const q = useQuery({ queryKey: QUERY_KEY, queryFn: () => api<Payload>('/v1/platform/cluster/manifests') })

  const payload = q.data
  const all = payload?.manifests ?? []
  const needle = filter.trim().toLowerCase()
  const rows = needle
    ? all.filter((row) =>
        [row.path, row.kind, row.name, row.component, row.purpose]
          .some((value) => value.toLowerCase().includes(needle)))
    : all

  const summary = payload?.summary
  const unproven = (summary?.pending ?? 0) + (summary?.invalid ?? 0)

  return (
    <>
      <PageHead
        title="แผนผังการติดตั้งบน Kubernetes"
        sub="ไฟล์ manifest ทุกไฟล์ที่ ship พร้อมผลการตรวจ schema — ไม่ใช่สถานะของคลัสเตอร์จริง"
        actions={
          <span className="chip">
            {summary ? `${summary.valid}/${summary.total} ผ่าน` : 'กำลังโหลด'}
          </span>
        }
      />

      <div className="card card-pad" style={{ marginBottom: 14, borderColor: 'var(--color-hairline)' }}>
        <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="chip gold">ยังไม่รันบนคลัสเตอร์</span>
          <span style={{ fontSize: 12.5 }}>
            {payload?.note ?? 'ตรวจสอบด้วย YAML parser, kubectl kustomize และ kubeconform เท่านั้น ไม่มีการ apply หรือ schedule ใด ๆ'}
          </span>
        </div>
      </div>

      {summary ? (
        <div className="grid grid-3" style={{ marginBottom: 14 }}>
          <Stat
            label="ไฟล์ทั้งหมด"
            value={summary.total}
            foot={`${summary.valid} ผ่าน · ${summary.skipped} ข้าม`}
          />
          <Stat
            label="ยังไม่มีหลักฐาน"
            value={unproven}
            foot={unproven === 0 ? 'ทุกไฟล์มีผลตรวจล่าสุด' : 'ต้องรัน validator ก่อนอ้างว่าผ่าน'}
          />
          <Stat
            label="เครื่องมือที่ใช้"
            value={payload?.validators.length ?? 0}
            foot={payload?.validators.join(' · ') || '—'}
          />
        </div>
      ) : null}

      <div className="row" style={{ gap: 10, marginBottom: 12, alignItems: 'center' }}>
        <input
          className="input mono"
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="กรองตาม path, kind หรือชื่อ object"
          aria-label="กรอง manifest"
          style={{ maxWidth: 420 }}
        />
        <span className="dim" style={{ fontSize: 11.5 }}>
          {rows.length} แถว
          {payload?.latest_validation ? ` · ตรวจล่าสุด ${thaiDateTime(payload.latest_validation)}` : ''}
        </span>
      </div>

      {q.isLoading ? (
        <div className="stack"><Skel h={56} /><Skel h={56} /><Skel h={56} /><Skel h={56} /></div>
      ) : q.isError ? (
        <div className="card card-pad">
          <div className="form-error">
            โหลดแคตตาล็อก manifest ไม่สำเร็จ — ตรวจสอบว่าเส้นทาง /v1/platform/cluster/manifests ถูก mount แล้ว
          </div>
        </div>
      ) : all.length === 0 ? (
        <Empty
          title="ยังไม่มีแคตตาล็อก"
          body="ตาราง platform.cluster_manifests ว่าง — apply migration 0115 แล้ว seed แถวจาก infra/k8s"
        />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>ไฟล์และวัตถุ</th>
                <th style={{ width: 110 }}>สถานะ</th>
                <th>ผู้ตรวจ</th>
                <th style={{ width: 160 }}>ตรวจเมื่อ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.path} style={{ cursor: 'default' }}>
                  <td>
                    <div className="mono" style={{ fontSize: 12, color: 'var(--color-ivory)' }}>{row.path}</div>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                      {row.kind}/{row.name}
                      {row.in_kustomization ? '' : ' · ไม่ได้อยู่ใน kustomization'}
                    </div>
                    <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>{row.purpose}</div>
                    <div className="mono dim" style={{ fontSize: 10.5, marginTop: 3 }} title={row.sha256}>
                      sha256 {row.sha256.slice(0, 12)}… · {row.bytes.toLocaleString('en-US')} bytes
                    </div>
                  </td>
                  <td>
                    <StatusChip result={row.validation.result} />
                  </td>
                  <td>
                    <div style={{ fontSize: 11.5 }}>{row.validation.validator || '—'}</div>
                    <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>{row.validation.detail || '—'}</div>
                  </td>
                  <td className="dim" style={{ fontSize: 11.5 }}>
                    {row.validation.validated_at ? thaiDateTime(row.validation.validated_at) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card card-pad" style={{ marginTop: 14 }}>
        <div className="card-label">ขอบเขตของหลักฐาน</div>
        <p style={{ fontSize: 12, lineHeight: 1.7, marginTop: 6 }}>
          ผ่านในหน้านี้หมายถึงไฟล์ถูกต้องตาม schema ของ Kubernetes และ render ผ่าน kustomize เท่านั้น
          ไม่ได้พิสูจน์ว่า image มีอยู่จริง, PVC จะ bind, probe จะผ่าน, HPA จะได้ metrics หรือ
          NetworkPolicy จะถูกบังคับใช้ — ทั้งหมดนั้นต้องรันบนคลัสเตอร์จริงและบันทึกแยก
        </p>
      </div>
    </>
  )
}
